// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import { jsx, ParamComponent } from 'panel';
import { IronContextRegistry } from 'common/report/context-registry';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { BannerEl } from 'common/components/banner';
import { BannerAction, BannerSize, BannerTheme, BannerType } from 'common/components/banner/types';
import { CustomAlertsCountResults } from 'app_api/projects/alerts/custom/types';
import { formatPercent } from 'common/util';
type AlertLimitBannerParams = {
  showManageButton?: boolean;
  showApproachingLimit?: boolean;
};

// Currently only anomaly detection limits are implemented. When regular alert limits are agreed upon this banner
// and the alertLimits context should be expanded upon to handle both alert type limits.
export class AlertLimitsBanner extends ParamComponent<AlertLimitBannerParams, EmptyObj, IronContextRegistry> {
  get config() {
    return {
      contexts: ["alertLimits" as const, "i18n" as const],
      params: {
        showManageButton: Boolean,
        showApproachingLimit: Boolean
      },
      defaultParams: {
        showManageButton: false,
        showApproachingLimit: false
      },
      template: () => {
        return <div>
            {this.shouldShowBanner() ? <BannerEl sel={".alert-limit-banner"} attrs={{
            theme: this.params.showApproachingLimit && this.isApproachingAlertLimit() ? BannerTheme.Info : BannerTheme.Warn,
            type: BannerType.Embedded,
            size: BannerSize.ExtraSmall,
            'header-text': this.getHeaderText(),
            'body-text': this.getBodyText(),
            'footer-text': this.getFooterText(),
            ctas: this.getCTAs(),
            'style-override': !this.params.showApproachingLimit ? ".banner-wrapper .banner-panel .banner-copy {margin-bottom: 3px;}" : null,
            'banner-tracking-type': "Custom Alerts Limit"
          }} /> : null}
          </div>;
      }
    };
  }
  get t() {
    return this.getContext("i18n").t;
  }
  isApproachingAlertLimit() {
    return this.isBelowLimit() && this.alertCount() >= this.alertLimit() - 10;
  }
  shouldShowBanner() {
    return !this.isBelowLimit() || this.params.showApproachingLimit && this.isApproachingAlertLimit();
  }
  getCTAs() {
    const ctas = [];
    if (this.params.showManageButton) {
      ctas.push({
        actionName: BannerAction.Manage,
        text: this.t("settings.user-settings.alerts.manage"),
        href: "/settings/#account/alert-settings"
      });
    }
    ctas.push({
      actionName: BannerAction.Help,
      text: this.t("settings.user-settings.alerts.why-limit"),
      href: "https://docs.mixpanel.com/docs/analysis/advanced/alerts#overview"
    });
    return ctas;
  }
  getFooterText() {
    let footerText = "";
    if (this.params.showApproachingLimit && this.shouldShowBanner()) {
      const alertCount = this.alertCount();
      const alertLimit = this.alertLimit();
      if (alertCount && alertLimit) {
        const alertPercentage = formatPercent(alertCount / alertLimit, {
          precision: 0
        });
        footerText = this.t("settings.user-settings.alerts.usage", {
          count: alertCount,
          max: alertLimit,
          percentage: alertPercentage
        });
      }
    }
    return footerText;
  }
  getBodyText() {
    let bodyText = "";
    const numOfAlerts = this.alertCount();
    const alertLimit = this.alertLimit();
    if (numOfAlerts >= alertLimit) {
      bodyText = this.t("settings.user-settings.alerts.reached-limit") + " " + this.t("settings.user-settings.alerts.delete-hint");
    } else if (this.params.showApproachingLimit && this.isApproachingAlertLimit()) {
      bodyText = this.t("settings.user-settings.alerts.approaching-limit") + " " + this.t("settings.user-settings.alerts.delete-hint");
    }
    return bodyText;
  }
  getHeaderText() {
    let headerText = "";
    if (!this.isBelowLimit()) {
      if (this.params.showApproachingLimit) {
        headerText = this.t("settings.user-settings.alerts.reached-title");
      } else {
        headerText = this.t("settings.user-settings.alerts.cant-create");
      }
    }
    return headerText;
  }
  getAlertLimitContext(): CustomAlertsCountResults {
    var _this$getContext$get$;
    return (_this$getContext$get$ = this.getContext("alertLimits").get().value) !== null && _this$getContext$get$ !== void 0 ? _this$getContext$get$ : null;
  }
  alertLimit() {
    var _this$getAlertLimitCo;
    return (_this$getAlertLimitCo = this.getAlertLimitContext()) === null || _this$getAlertLimitCo === void 0 ? void 0 : _this$getAlertLimitCo.alert_limit;
  }
  alertCount() {
    var _this$getAlertLimitCo2;
    return (_this$getAlertLimitCo2 = this.getAlertLimitContext()) === null || _this$getAlertLimitCo2 === void 0 ? void 0 : _this$getAlertLimitCo2.anomaly_alerts_count;
  }
  isBelowLimit() {
    var _this$getAlertLimitCo3, _this$getAlertLimitCo4;
    return (_this$getAlertLimitCo3 = (_this$getAlertLimitCo4 = this.getAlertLimitContext()) === null || _this$getAlertLimitCo4 === void 0 ? void 0 : _this$getAlertLimitCo4.is_below_limit) !== null && _this$getAlertLimitCo3 !== void 0 ? _this$getAlertLimitCo3 : true;
  }
}
export const AlertLimitsBannerEl = defineAndMakeJsxFactory<any>("alert-limits-banner", AlertLimitsBanner);